import { useMemo, useState } from 'react';
import { MdCalendarToday } from 'react-icons/md';
import { format } from 'date-fns';
import clsx from 'clsx';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import DateRangeCalendar, { type DateRangeValue } from '@/components/DateRangeCalendar';
import { dateFormat } from '@/utils/dateFormat';

type Props = {
  from: string;
  to: string;
  onChange: (from: string, to: string) => void;
};

function parseDateString(value: string): Date | null {
  if (!value) return null;
  const [year, month, day] = value.split('-').map(Number);
  if (!year || !month || !day) return null;
  return new Date(year, month - 1, day);
}

function formatDateParam(date: Date): string {
  return format(date, 'yyyy-MM-dd');
}

export default function EntryDateRangePicker({ from, to, onChange }: Props) {
  const [open, setOpen] = useState(false);
  const currentRange = useMemo<DateRangeValue>(
    () => ({ start: parseDateString(from), end: parseDateString(to) }),
    [from, to]
  );
  const [draftRange, setDraftRange] = useState<DateRangeValue>(currentRange);

  const hasSelection = Boolean(currentRange.start || currentRange.end);
  const displayText = hasSelection
    ? `${currentRange.start ? dateFormat(currentRange.start) : ''} ~ ${
        currentRange.end ? dateFormat(currentRange.end) : ''
      }`
    : '全て';

  const canSubmit = Boolean(draftRange.start && draftRange.end);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setDraftRange(currentRange);
    }
    setOpen(nextOpen);
  };

  const handleSubmit = () => {
    if (!draftRange.start || !draftRange.end) return;
    onChange(formatDateParam(draftRange.start), formatDateParam(draftRange.end));
    setOpen(false);
  };

  const handleReset = () => {
    setDraftRange({ start: null, end: null });
    onChange('', '');
    setOpen(false);
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label="入社予定日の期間を選択"
          aria-haspopup="dialog"
          className={clsx(
            'flex h-[42px] w-full items-center justify-between gap-2 rounded-sm border border-solid border-border bg-white px-3 focus-visible:outline-hidden',
            open && 'border-2 border-primary'
          )}
        >
          <span
            className={clsx(
              'truncate text-sm leading-[1.7] whitespace-nowrap',
              hasSelection ? 'text-main' : 'text-placeholder'
            )}
          >
            {displayText}
          </span>
          <MdCalendarToday className="size-icon-sm shrink-0 text-placeholder" />
        </button>
      </PopoverTrigger>

      <PopoverContent align="start" sideOffset={6} className="w-auto rounded p-0">
        <div className="bg-white p-3">
          <DateRangeCalendar value={draftRange} onChange={setDraftRange} />
        </div>

        <div className="flex items-center justify-end gap-2 border-t border-solid border-border px-3 py-2">
          <button
            type="button"
            className="h-8 rounded border border-solid border-border bg-white px-3 text-xs font-bold text-main hover:bg-gray-50"
            onClick={handleReset}
          >
            リセット
          </button>
          <button
            type="button"
            disabled={!canSubmit}
            className="h-8 rounded border-0 bg-primary px-3 text-xs font-bold text-white hover:bg-primary_darken disabled:cursor-not-allowed disabled:opacity-40"
            onClick={handleSubmit}
          >
            決定
          </button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
